import React, { createContext, useContext, useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { DifficultyBadge } from "../../../components/Difficulty/Difficulty";
import { isBlankHtml, RichText } from "../../../components/KaTeXComponent/RichText";
import { formatDateTime } from "../../../utils/formatDate";
import { cardClass } from "../../Exams/TestBuilder/ui";
import {
  AnswerOption,
  ContentSkeleton,
  LoadError,
  VersionByline,
  VersionStatus,
  reasonLabel,
  versionLabel,
} from "./parts";
import {
  MARK_CLASSES,
  VERSION_FIELDS,
  correctLetters,
  diffPlainText,
  diffRichText,
  fieldChanged,
  pairAnswerKeys,
} from "./versionDiff";

// "Бұрын · Нұсқа 2" and "Кейін · Нұсқа 5": what each side is, written over each cell where the two sides stack
// (a phone); side by side, the pickers above the columns say it.
const SidesContext = createContext({ old: "", new: "" });

const CELL_TONES = {
  plain: "bg-white ring-slate-200",
  text: "bg-slate-50 ring-slate-200",
  removed: "bg-red-50/70 ring-red-200",
  added: "bg-emerald-50/70 ring-emerald-200",
};

function SideLabel({ side }) {
  const sides = useContext(SidesContext);
  return <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 md:hidden">{sides[side]}</p>;
}

function Cell({ side, tone = "plain", children }) {
  return (
    <div className="min-w-0">
      <SideLabel side={side} />
      <div className={`min-w-0 rounded-xl p-3 ring-1 ring-inset sm:p-4 ${CELL_TONES[tone]}`}>{children}</div>
    </div>
  );
}

const NotSet = () => {
  const { t } = useTranslation();
  return <span className="text-sm italic text-slate-500">{t("question_versions.not_set")}</span>;
};

// Subtopic, difficulty, term, objective: the value of each side, tinted when it changed.
function ValuePair({ field, before, after, changed }) {
  const value = (data) => {
    if (field === "difficulty") return data.difficulty ? <DifficultyBadge level={data.difficulty} withLabel /> : null;
    const labels = data.labels || {};
    if (field === "subtopic") {
      const path = [labels.topic, labels.subtopic].filter(Boolean).join(" › ");
      return data.subtopic_id && path ? <span className="break-words text-sm text-slate-800">{path}</span> : null;
    }
    return data[`${field}_id`] && labels[field] ? <span className="break-words text-sm text-slate-800">{labels[field]}</span> : null;
  };
  return (
    <div className="grid gap-3 md:grid-cols-2 md:gap-4">
      <Cell side="old" tone={changed ? "removed" : "plain"}>{value(before) || <NotSet />}</Cell>
      <Cell side="new" tone={changed ? "added" : "plain"}>{value(after) || <NotSet />}</Cell>
    </div>
  );
}

function PlainRuns({ runs, side }) {
  const Mark = side === "old" ? "del" : "ins";
  return runs.map((run, index) =>
    run.changed ? (
      <Mark key={index} className={MARK_CLASSES[side]}>
        {run.text}
      </Mark>
    ) : (
      <React.Fragment key={index}>{run.text}</React.Fragment>
    )
  );
}

function NamePair({ before, after, changed }) {
  const diff = useMemo(() => (changed ? diffPlainText(before.name, after.name) : null), [changed, before.name, after.name]);
  const side = (data, key) =>
    data.name ? (
      <p className="break-words text-sm text-slate-800">{diff ? <PlainRuns runs={diff[key]} side={key} /> : data.name}</p>
    ) : (
      <NotSet />
    );
  return (
    <div className="grid gap-3 md:grid-cols-2 md:gap-4">
      <Cell side="old">{side(before, "old")}</Cell>
      <Cell side="new">{side(after, "new")}</Cell>
    </div>
  );
}

function BodyPair({ before, after, changed }) {
  const { t } = useTranslation();
  const diff = useMemo(() => (changed ? diffRichText(before.body, after.body) : null), [changed, before.body, after.body]);
  const side = (data, key) =>
    isBlankHtml(data.body) ? (
      <p className="text-sm italic text-slate-500">{t("question_versions.empty_body")}</p>
    ) : (
      // Pictures kept small enough to see both sides at once; the version itself shows them full size.
      <div className="break-words text-base leading-relaxed text-slate-900 [&_img]:max-h-96 [&_img]:!w-auto">
        <RichText html={diff ? diff[key] : data.body} />
      </div>
    );
  return (
    <div className="space-y-3">
      {diff && !diff.words && <p className="text-xs text-slate-500">{t("question_versions.format_only")}</p>}
      <div className="grid gap-3 md:grid-cols-2 md:gap-4">
        <Cell side="old" tone="text">{side(before, "old")}</Cell>
        <Cell side="new" tone="text">{side(after, "new")}</Cell>
      </div>
    </div>
  );
}

function ChangeBadge({ kind }) {
  const { t } = useTranslation();
  const tone = kind === "added" ? "bg-emerald-100 text-emerald-800 ring-emerald-600/20" : "bg-red-100 text-red-800 ring-red-600/20";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${tone}`}>
      {t(`question_versions.${kind}`)}
    </span>
  );
}

// An option one side does not have (side by side only: where the sides stack, the option's tag says it).
function EmptySlot() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[3rem] items-center justify-center rounded-lg border border-dashed border-slate-300 text-sm text-slate-400">
      <span aria-hidden="true">—</span>
      <span className="sr-only">{t("question_versions.none")}</span>
    </div>
  );
}

function AnswerRow({ row }) {
  const { t } = useTranslation();
  const diff = useMemo(
    () => (row.change === "changed" && row.old.body !== row.new.body ? diffRichText(row.old.body, row.new.body) : null),
    [row]
  );
  const changedNote = row.change === "changed" && <span className="sr-only">{t("question_versions.changed")}</span>;
  const cell = (key) => {
    const answer = row[key];
    if (!answer) return <EmptySlot />;
    const own = (key === "old" && row.change === "removed") || (key === "new" && row.change === "added");
    return (
      <AnswerOption answer={answer} html={diff?.[key]} tone={own ? row.change : row.change === "changed" ? "changed" : "plain"}>
        {own && <ChangeBadge kind={row.change} />}
        {changedNote}
      </AnswerOption>
    );
  };
  // Where the sides stack, an option the same on both shows once, and an added or removed one only on its side.
  return (
    <div className="grid gap-2 md:grid-cols-2 md:gap-4">
      <div className={`min-w-0 ${row.change && row.old ? "" : "hidden md:block"}`}>
        {row.change && <SideLabel side="old" />}
        {cell("old")}
      </div>
      <div className={`min-w-0 ${row.new ? "" : "hidden md:block"}`}>
        {row.change && <SideLabel side="new" />}
        {cell("new")}
      </div>
    </div>
  );
}

function AnswersPair({ before, after }) {
  const { t } = useTranslation();
  const rows = useMemo(() => pairAnswerKeys(before.answer_keys, after.answer_keys), [before.answer_keys, after.answer_keys]);
  const none = t("question_versions.none");
  const correctBefore = correctLetters(before.answer_keys).join(", ");
  const correctAfter = correctLetters(after.answer_keys).join(", ");
  return (
    <div className="space-y-3">
      {correctBefore !== correctAfter && (
        <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900 ring-1 ring-inset ring-amber-600/20">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
          {t("question_versions.correct_changed", { from: correctBefore || none, to: correctAfter || none })}
        </p>
      )}
      {rows.length ? (
        <div className="space-y-3 md:space-y-2">
          {rows.map((row) => (
            <AnswerRow key={row.key} row={row} />
          ))}
        </div>
      ) : (
        <p className="text-sm italic text-slate-500">{t("question_versions.no_options")}</p>
      )}
    </div>
  );
}

function FieldSection({ field, before, after, changed }) {
  const { t } = useTranslation();
  const titleId = useId();
  return (
    <section aria-labelledby={titleId} className="space-y-3 border-t border-slate-100 pt-5">
      <div className="flex flex-wrap items-center gap-2">
        <h4 id={titleId} className="text-sm font-semibold text-slate-900">
          {t(`question_versions.fields.${field}`)}
        </h4>
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
            changed ? "bg-amber-50 text-amber-800 ring-amber-600/20" : "bg-slate-100 text-slate-600 ring-slate-500/20"
          }`}
        >
          {t(changed ? "question_versions.changed" : "question_versions.unchanged")}
        </span>
      </div>
      {field === "body" ? (
        <BodyPair before={before} after={after} changed={changed} />
      ) : field === "answer_keys" ? (
        <AnswersPair before={before} after={after} />
      ) : field === "name" ? (
        <NamePair before={before} after={after} changed={changed} />
      ) : (
        <ValuePair field={field} before={before} after={after} changed={changed} />
      )}
    </section>
  );
}

function Legend() {
  const { t } = useTranslation();
  return (
    <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
      <span className="inline-flex items-center gap-1.5">
        <span className="h-3 w-5 rounded-sm bg-red-100 ring-1 ring-inset ring-red-300" aria-hidden="true" />
        {t("question_versions.legend_removed")}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="h-3 w-5 rounded-sm bg-emerald-100 ring-1 ring-inset ring-emerald-300" aria-hidden="true" />
        {t("question_versions.legend_added")}
      </span>
    </p>
  );
}

// The fields of two loaded versions: those that changed (all with "show unchanged"), each side by side.
function Comparison({ before, after, showAll, onShowAll }) {
  const { t } = useTranslation();
  // A question's name is rarely set (imports set it): left out while neither version has one.
  const fields = VERSION_FIELDS.filter((field) => field !== "name" || before.name || after.name);
  const changed = fields.filter((field) => fieldChanged(field, before, after));
  const shown = showAll ? fields : changed;
  const marked = changed.some((field) => field === "body" || field === "answer_keys" || field === "name");

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        {changed.length ? (
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-slate-700">{t("question_versions.changed_fields")}</span>
            {changed.map((field) => (
              <span
                key={field}
                className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-600/20"
              >
                {t(`question_versions.fields.${field}`)}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-600">{t("question_versions.nothing_changed")}</p>
        )}
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(event) => onShowAll(event.target.checked)}
            className="h-4 w-4 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-600"
          />
          {t("question_versions.show_unchanged")}
        </label>
      </div>
      {marked && <Legend />}
      {shown.map((field) => (
        <FieldSection key={field} field={field} before={before} after={after} changed={changed.includes(field)} />
      ))}
    </div>
  );
}

function VersionPicker({ label, value, other, row, versions, onChange }) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const known = versions.some((version) => version.number === value);
  return (
    <div className="min-w-0 space-y-1.5">
      <label htmlFor={id} className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </label>
      <select
        id={id}
        value={known ? value : ""}
        onChange={(event) => onChange(Number(event.target.value))}
        className="block w-full rounded-lg border-slate-300 py-2 pl-3 pr-10 text-sm text-slate-900 shadow-sm transition duration-200 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15"
      >
        {!known && (
          <option value="" disabled>
            {versionLabel(t, value)}
          </option>
        )}
        {versions.map((version) => (
          <option key={version.number} value={version.number} disabled={version.number === other}>
            {`${versionLabel(t, version.number)} · ${reasonLabel(t, version.reason)} · ${formatDateTime(version.created, i18n.language)}`}
          </option>
        ))}
      </select>
      {row && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <VersionByline version={row} />
          <VersionStatus code={row.status_code} />
        </div>
      )}
    </div>
  );
}

/**
 * Two versions side by side, the older on the left: `from` and `to` are their numbers (in either order),
 * `versionState`/`request` read their content, `onChange(a, b)` picks another pair.
 */
export default function VersionCompare({ versions, from, to, versionState, request, onChange, headingRef }) {
  const { t } = useTranslation();
  const titleId = useId();
  // Kept while other pairs are picked.
  const [showAll, setShowAll] = useState(false);
  const older = Math.min(from, to);
  const newer = Math.max(from, to);
  const rowOld = versions.find((version) => version.number === older) || null;
  const rowNew = versions.find((version) => version.number === newer) || null;
  const same = older === newer;
  const missing = [rowOld ? null : older, rowNew ? null : newer].find((number) => number !== null) ?? null;

  useEffect(() => {
    if (same) return;
    if (rowOld) request(older);
    if (rowNew) request(newer);
  }, [request, older, newer, same, rowOld, rowNew]);

  const stateOld = versionState(older);
  const stateNew = versionState(newer);
  const failed = [
    [older, stateOld],
    [newer, stateNew],
  ].find(([, state]) => state.error);
  const sides = useMemo(
    () => ({
      old: `${t("question_versions.older")} · ${versionLabel(t, older)}`,
      new: `${t("question_versions.newer")} · ${versionLabel(t, newer)}`,
    }),
    [t, older, newer]
  );

  let body;
  if (same) {
    body = <p className="text-sm text-slate-600">{t("question_versions.same_version")}</p>;
  } else if (missing !== null) {
    body = <LoadError message={t("question_versions.version_not_found", { number: missing })} />;
  } else if (failed) {
    const [number, state] = failed;
    const notFound = state.error?.response?.status === 404;
    body = (
      <LoadError
        message={t(notFound ? "question_versions.version_not_found" : "question_versions.version_failed", { number })}
        onRetry={notFound ? undefined : () => request(number)}
      />
    );
  } else if (stateOld.data && stateNew.data) {
    body = (
      <Comparison before={stateOld.data.data || {}} after={stateNew.data.data || {}} showAll={showAll} onShowAll={setShowAll} />
    );
  } else {
    body = (
      <div role="status">
        <span className="sr-only">{t("question_detail.loading")}</span>
        <ContentSkeleton lines={3} />
      </div>
    );
  }

  return (
    <SidesContext.Provider value={sides}>
      <article aria-labelledby={titleId} className={`${cardClass} min-w-0`}>
        <header className="space-y-4 border-b border-slate-100 p-5 sm:p-6">
          <h3
            id={titleId}
            ref={headingRef}
            tabIndex={-1}
            className="text-xl font-semibold tracking-tight text-slate-900 focus:outline-none"
          >
            {t("question_versions.compare_title")}
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            <VersionPicker
              label={t("question_versions.older")}
              value={older}
              other={newer}
              row={rowOld}
              versions={versions}
              onChange={(number) => onChange(number, newer)}
            />
            <VersionPicker
              label={t("question_versions.newer")}
              value={newer}
              other={older}
              row={rowNew}
              versions={versions}
              onChange={(number) => onChange(older, number)}
            />
          </div>
        </header>
        <div className="p-5 sm:p-6">{body}</div>
      </article>
    </SidesContext.Provider>
  );
}
